import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig } from '@inavista/components/grid';

// My Cart (ISC Execution tab → parts catalog, and the Parts Catalog page) on @inavista/components. Same checkbox
// column, columns, labels, order, widths, alignment, formats, links, quantity stepper and Delete action as the
// Syncfusion grid it replaces. Rows: POST ISCPartsOrder/GetUserCartItems (unchanged), held by the page and served
// by LocalRowsGridApi. No paging (Martin): the grid scrolls.

export const CART_URL = 'page://parts-catalog-cart';
export const CART_PERSISTENCE_KEY = 'rims.service.parts-catalog-popup';
export const DELETE_ITEM_ACTION = 'Delete Item';

// ISCCartItems, in the old grid's column order. `id` is the key (hidden, as before).
export const CART_COLUMNS: [string, string][] = [
  ['id', 'bigint'],
  ['item', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['stock', 'float'],
  ['quantity', 'float'],
  ['itemPrice', 'float'],
  ['grossAmount', 'float'],
  ['itemDiscount', 'float'],
  ['netPrice', 'float'],
  ['expectedDeliveryDate', 'datetime'],
];

const ORDER = CART_COLUMNS.map(([c]) => c).filter(c => c !== 'id');
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

// The old command column: one "Delete Item" trash can, 130px.
export const CART_ACTION_ICONS: ActionIconDef[] = [
  { key: DELETE_ITEM_ACTION, label: 'Delete Item', icon: TRASH_ICON, colorFn: () => '#000000de' },
];

// The old templates: '$' and two decimals ('$' alone when there is no value).
const money = (value: unknown) => `$${value == null || value === '' ? '' : Number(value).toFixed(2)}`;

export interface CartGridContext {
  /** The old Stocks template: `stock` for a USD user, else `stockCan`. */
  stockOf: (row: Record<string, unknown>) => unknown;
  /** The old Order Qty template: − / number / +. */
  quantityTemplate: TemplateRef<unknown>;
}

export function buildCartGridConfig(ctx: CartGridContext): GridConfig {
  const center = { horizontal: 'center' as const, header: { horizontal: 'center' as const } };
  const left = { horizontal: 'left' as const, header: { horizontal: 'left' as const } };
  return {
    apiBaseUrl: CART_URL,
    clientSide: true,
    keyColumn: 'id',
    rowHeight: 30,
    // The old checkbox column (multi-select, checkbox only), 55 wide.
    selectable: true,
    selectColumnWidth: 55,
    defaultColumnOrder: ORDER,
    hiddenByDefaultColumns: ['id'],
    columnLabels: {
      item: 'Item.', itemDescription: 'Description', stock: 'Stocks', quantity: 'Order Qty', itemPrice: 'Price',
      grossAmount: 'Gross Amount', itemDiscount: 'Discount', netPrice: 'Net Amount',
      expectedDeliveryDate: 'Estimated Delivery Date',
    },
    // Stocks and Order Qty centred, the rest left, as the old grid (Syncfusion aligned the header with the cells).
    columnAlign: Object.fromEntries(ORDER.map(c => [c, c === 'stock' || c === 'quantity' ? center : left])),
    // The old widths; Order Qty had none and took the rest.
    columnWidths: Object.fromEntries(ORDER.map(c => [c, c === 'quantity' ? 160 : 120])),
    stretchColumns: true,
    columnSizing: Object.fromEntries(ORDER.filter(c => c !== 'quantity').map(c => [c, { noStretch: true }])),
    columnTypes: { expectedDeliveryDate: 'date' },
    columnDateFormats: { expectedDeliveryDate: 'd MMM, y' },
    columnFormatters: {
      stock: (_value, row) => String(ctx.stockOf(row) ?? ''),
      itemPrice: money, grossAmount: money, itemDiscount: money, netPrice: money,
    },
    cellTemplates: { quantity: ctx.quantityTemplate },
    // Item and Description open the Item Information popup, as before.
    hyperlinkColumns: ['item', 'itemDescription'],
    // The old Actions column was 130px wide.
    actionsColumnMinWidth: 130,
    // The old grid had no toolbar (no search, no column chooser, no Excel button). Column filters (menu) and sort
    // stay; the toolbar holds only Clear filters and Reset layout, which undoes the saved layout.
    enableSearch: false,
    enableColumnsPicker: false,
    enableExport: false,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableRefresh: false,
    enableResetLayout: true,
    enableFooter: false,
    emptyStateMessage: 'Your cart is empty.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CART_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
